export const TASK_CREATE_DESCRIPTION = `使用此工具为当前编码会话创建结构化任务列表。它可以帮助你跟踪进度、组织复杂任务，并向用户体现你处理任务的完整性与严谨性
它也能帮助用户理解当前任务的推进情况，以及他们整体请求的完成进度

## 何时使用此工具

在以下场景中，应主动使用此工具：

- 复杂的多步骤任务：当任务需要 3 个或更多彼此独立的步骤或操作时
- 非平凡的复杂任务：需要仔细规划或多次操作的任务
- 规划模式：在使用 plan mode 时，创建任务列表来跟踪工作进展
- 用户明确要求 todo list：当用户直接要求你使用任务列表时
- 用户提供了多个任务：当用户给出一组待办事项时，例如编号列表或逗号分隔的多个任务
- 收到新的指令后：立即将用户的新要求记录为任务
- 开始处理某个任务时：在开始工作之前，先将该任务标记为 in_progress
- 完成某个任务后：将该任务标记为 completed，并补充实现过程中发现的后续任务

## 何时不使用此工具

在以下情况下，应跳过此工具：
- 只有一个简单直接的任务
- 任务非常简单，使用任务追踪不会带来组织上的收益
- 任务可以在少于 3 个简单步骤内完成
- 任务只是纯对话或信息性问答

注意：如果只有一个简单任务要做，就不应该使用此工具。此时直接完成任务会更合适

## 任务字段

- **subject**：简短、可执行的任务标题，使用祈使句形式，例如 “修复登录流程中的认证错误”
- **description**：说明需要完成的具体内容
- **activeForm**（可选）：任务处于 in_progress 状态时，在加载指示器中显示的进行时表述，例如 “Fixing authentication bug”。如果省略，则加载指示器会显示 subject。

所有任务在创建时的状态均为 \`pending\`

## 使用建议

- 创建任务时，使用清晰、具体的 subject，描述该任务要达成的结果
- 创建任务后，如有需要，使用 TaskUpdate 设置 blockedBy 依赖关系
- 先检查 TaskList，避免创建重复任务`;

export const TASK_GET_DESCRIPTION = `使用此工具，通过任务 ID 从任务列表中检索对应任务

## 何时使用此工具

- 当你在开始处理某个任务前，需要查看完整描述和上下文时
- 当你需要了解任务之间的依赖关系时，例如它阻塞了哪些任务、又被哪些任务阻塞
- 当你被分配到某个任务后，需要获取完整需求时

## 输出内容

返回完整的任务详情：

- **subject**：任务标题
- **description**：详细需求和上下文
- **status**：任务状态，可为 'pending'、'in_progress' 或 'completed'
- **blocks**：正在等待该任务完成的其他任务
- **blockedBy**：该任务开始前必须先完成的其他任务

## 使用建议

- 获取任务后，在开始工作前，先确认其 blockedBy 列表为空。
- 使用 TaskList 可以以摘要形式查看所有任务。
`;

export const TASK_UPDATE_DESCRIPTION = `使用此工具更新任务列表中的任务

## 何时使用此工具

**将任务标记为已完成：**
- 当你已经完成某个任务所描述的工作时
- 当某个任务已不再需要，或已被其他任务取代时
- 重要：完成分配给你的任务后，务必将其标记为已完成
- 标记完成后，调用 TaskList 查找下一个任务

- 只有在任务已经被完全完成时，才可以将其标记为 completed
- 如果遇到错误、阻塞，或无法完成任务，请保持任务状态为 in_progress
- 当任务被阻塞时，创建一个新任务，说明需要先解决的问题
- 在以下情况下，绝不要将任务标记为 completed：
  - 测试仍然失败
  - 实现还不完整
  - 存在尚未解决的错误
  - 无法找到必要的文件或依赖

**删除任务：**
- 当某个任务已不再相关，或是误创建的任务时
- 将 status 设置为 \`deleted\` 会永久移除该任务

**更新任务详情：**
- 当需求发生变化，或变得更加明确时
- 当需要建立任务之间的依赖关系时

## 可更新字段

- **status**：任务状态，参见下方“状态流转”
- **subject**：修改任务标题，使用祈使句形式，例如“Run tests”
- **description**：修改任务描述
- **activeForm**：任务处于 in_progress 状态时，在加载提示中显示的现在进行时形式，例如“Running tests”
- **metadata**：将指定的 metadata 键合并到任务中；将某个键设置为 null 可删除该键
- **blockedBy**：设置当前任务必须等待哪些任务完成后才能开始；传 null 或空数组可清空依赖

## 状态流转

状态按如下顺序推进：\`pending\` → \`in_progress\` → \`completed\`

使用 \`deleted\` 可永久移除任务。

## 状态新鲜度

在更新任务之前，务必先使用 \`TaskGet\` 读取该任务的最新状态。

## 示例

开始处理任务时，将任务标记为进行中：
\`\`\`json
{"taskId": "1", "status": "in_progress"}
\`\`\`

完成任务后，将任务标记为已完成：
\`\`\`json
{"taskId": "1", "status": "completed"}
\`\`\`

删除任务：
\`\`\`json
{"taskId": "1", "status": "deleted"}
\`\`\`

设置任务依赖关系：
\`\`\`json
{"taskId": "2", "blockedBy": ["1"]}
\`\`\`
`;

export const TASK_LIST_DESCRIPTION = `使用此工具列出任务列表中的所有任务

## 何时使用此工具

- 查看当前有哪些任务可以处理（状态为 'pending'，且未被阻塞）
- 检查项目的整体进度
- 找出被阻塞、需要先解决依赖关系的任务
- 完成一个任务后，检查是否有新的任务解除阻塞
- 当有多个任务可处理时，**优先按任务 ID 从小到大处理**，因为较早的任务通常会为后续任务建立必要上下文

## 输出内容

返回每个任务的摘要信息：

- **id**：任务标识符，可用于 TaskGet、TaskUpdate
- **subject**：任务的简要说明
- **status**：任务状态，可能是 'pending'、'in_progress' 或 'completed'
- **blockedBy**：尚未完成、且必须先解决的任务 ID 列表；存在 blockedBy 的任务在依赖解决前不应开始

如需查看某个任务的完整信息，包括详细描述和评论，请使用 TaskGet 并传入具体的任务 ID。
`;
